<script setup lang="ts">
    import { columns } from '@/tables/columns/dashboard';
    import { Head, router } from '@inertiajs/vue3';
    import { onBeforeUnmount, onMounted, watchEffect, ref, watch, shallowRef } from 'vue';
    import { Separator } from '@/components/ui/separator';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DashboardStats from '@/components/dashboard/DashboardStats.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableAdvancedViewOptions from '@/components/datatable/DataTableAdvancedViewOptions.vue';
    import DataTableRefreshButton from '@/components/datatable/DataTableRefreshButton.vue';
    import MonthPicker from '@/components/MonthPicker.vue';
    import PageCard from '@/components/PageCard.vue';
    import TableTypeSelect from '@/components/datatable/DataTableTypeSelect.vue';
    import type {
        BreadcrumbItem,
        SelectOption,
        SharedData,
        UserTableViewOption,
        DashboardTableData,
    } from '@/types';
    import QueryKey from '@/enums/QueryKey';
    import { useViewOptionsStore } from '@/stores/view-options';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import type { Table } from '@tanstack/vue-table';
    import { registerDashboardTableRefetch } from '@/composables/dashboardTableRefresh';

    const { hasPermission } = usePermissions();

    const props = defineProps<{
        stats: Record<string, number>;
        statusOptions: Record<string, SelectOption[]>;
        viewOptions: UserTableViewOption[];
        activeViewOptionId: number | null;
    }>();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.title',
            href: route('v2.dashboard.index'),
        },
    ];

    const page = usePage<SharedData>();

    const viewOptionsStore = useViewOptionsStore();

    const selectedGrouping = ref('my_clients');
    
    const STORAGE_KEY = `KC_dashboard_selectedGrouping`;
    const savedSelectedGrouping = localStorage.getItem(STORAGE_KEY);
    if (savedSelectedGrouping) {
        selectedGrouping.value = savedSelectedGrouping;
    }

    const reportingPeriod = ref(page.props.auth.user.reporting_full_date);

    const dataTableRef = ref<{ table: Table<DashboardTableData> } | undefined>(undefined);
    const tableData = shallowRef<DashboardTableData[]>([]);
    const hydrating = ref(false);
    const hasHydratedOnce = ref(false);
    const manualRefetching = ref(false);
    const hydratedThisCycle = ref(false);
    const hydrationInFlight = ref(false);

    const { isPending, isFetching, isRefetching, isError, data, refetch } = useQuery({
        queryKey: [QueryKey.EMPLOYEE_DASHBOARD, { selectedGrouping, reportingPeriod }],
        queryFn: getCompanies,
    });

    async function handleRefetch(options: { forceHydration?: boolean } = {}) {
        manualRefetching.value = true;

        try {
            // Call table-basic directly to ensure we hit the endpoint and get
            // whatever cached/prehydrated rows the server can return.
            const rows = (await getCompanies()) as DashboardTableData[];

            if (!rows || !rows.length) {
                return;
            }

            // Replace visible rows for manual refresh so user sees new data
            tableData.value = rows;

            // If the server already returned prehydrated rows, skip client
            // details request and mark hydration done.
            const prehydrated = rowsArePrehydrated(tableData.value as DashboardTableData[]);
            if (prehydrated && !options.forceHydration) {
                hasHydratedOnce.value = true;
                hydratedThisCycle.value = true;

                return;
            }

            // Normal manual reload keeps the old in-flight guard; modal saves force fresh details.
            if (options.forceHydration || !hydrationInFlight.value) {
                hydrationInFlight.value = true;
                hydrating.value = true;
                try {
                    await loadCompanyDetails(tableData.value);
                } finally {
                    hydrationInFlight.value = false;
                    hydrating.value = false;
                    hasHydratedOnce.value = true;
                }
            }
        } catch (e) {
            console.error('Manual refetch failed', e);
        } finally {
            manualRefetching.value = false;
        }
    }

    const handleDashboardManualRefetch = (event: Event) => {
        const forceHydration = (event as CustomEvent<{ forceHydration?: boolean }>).detail?.forceHydration ?? false;

        void handleRefetch({ forceHydration });
    };

    // Allow other components (e.g., bulk updater) to force a manual-style refetch with loader.
    onMounted(() => {
        registerDashboardTableRefetch((options) => void handleRefetch(options));
        window.addEventListener('dashboard-manual-refetch', handleDashboardManualRefetch);
    });

    onBeforeUnmount(() => {
        registerDashboardTableRefetch(null);
        window.removeEventListener('dashboard-manual-refetch', handleDashboardManualRefetch);
    });

    async function getCompanies() {
        const { data } = await axios.get(
            route('v2.dashboard.table-basic', {
                reportingPeriod: reportingPeriod.value,
                grouping: selectedGrouping.value,
            }),
        );

        return data as DashboardTableData[];
    }

    function buildIdToRowMap(rows: DashboardTableData[]) {
        const map = new Map<number, any>();
        const walk = (r: any) => {
            if (r && typeof r.id !== 'undefined') {
                map.set(r.id as number, r);
            }
            if (Array.isArray(r?.subRows)) {
                r.subRows.forEach(walk);
            }
        };
        rows.forEach(walk);

        return map;
    }

    function applyDetailsImmutable(rows: DashboardTableData[], details: Record<string, DashboardTableData>): DashboardTableData[] {
        const updateNode = (node: any): any => {
            if (Array.isArray(node?.subRows)) {
                return {
                    ...node,
                    subRows: node.subRows.map(updateNode),
                };
            }

            const id = (node as any)?.id;
            if (typeof id === 'number') {
                const d = details[String(id)];
                if (d) {
                    const next: any = { ...node };
                    for (const [k, v] of Object.entries(d)) {
                        if (k === 'id' || k === 'name' || k === 'subRows') continue;
                        next[k] = v as any;
                    }
                    return next as DashboardTableData;
                }
            }

            return node;
        };

        return rows.map(updateNode) as DashboardTableData[];
    }

    function rowsArePrehydrated(rows: DashboardTableData[]) {
        const walk = (node) => {
            if (Array.isArray(node?.subRows)) {
                for (const sub of node.subRows) {
                    const res = walk(sub);
                    if (res !== null) return res;
                }
                return null;
            }

            if (typeof node?.id !== 'undefined') {
                // If bookkeeping_status or status_id exists and is not null, treat as prehydrated
                if (typeof node.bookkeeping_status !== 'undefined' && node.bookkeeping_status !== null) return true;
                if (typeof node.status_id !== 'undefined' && node.status_id !== null) return true;
                return false;
            }

            return null;
        };

        for (const r of rows) {
            const res = walk(r);
            if (res !== null) return res;
        }

        return false;
    }


    function mergeBasicPreservingDetails(rows: DashboardTableData[]) {
        const existingMap = buildIdToRowMap(tableData.value);

        const mapNode = (node: any): any => {
            if (Array.isArray(node?.subRows)) {
                return {
                    name: node.name,
                    subRows: node.subRows.map(mapNode),
                };
            }

            const id = (node as any)?.id;
            if (typeof id === 'number') {
                const existing = existingMap.get(id);
                if (existing) {
                    return {
                        ...existing,
                        id: node.id,
                        name: node.name,
                        keepers_employees: (node as any)?.keepers_employees ?? (existing as any)?.keepers_employees ?? [],
                        comment_count: (node as any)?.comment_count ?? (existing as any)?.comment_count ?? 0,
                    } as DashboardTableData;
                }

                return node as DashboardTableData;
            }

            return node;
        };

        return rows.map(mapNode) as DashboardTableData[];
    }



async function loadCompanyDetails(rows: DashboardTableData[]) {
        const idMap = buildIdToRowMap(rows);
        const ids = Array.from(idMap.keys());
        if (ids.length === 0) return;

        try {
            const { data: details } = await axios.post(route('v2.dashboard.table-details'), {
                company_ids: ids,
                reportingPeriod: reportingPeriod.value,
            });

            // Apply details immutably to avoid readonly proxy pitfalls and ensure reactivity
            tableData.value = applyDetailsImmutable(tableData.value, details as Record<string, DashboardTableData>);
        } catch (e) {
            console.error('Failed to load company details', e);
        }
    }



    const typeOptions = [
        { value: 'my_clients', label: 'dashboards.grouping.my_clients' },
        { value: 'all', label: 'dashboards.grouping.all' },
        { value: 'outsourcing', label: 'dashboards.grouping.outsourcing' },
        { value: 'powerbi', label: 'dashboards.grouping.powerbi' },
        { value: 'salary', label: 'dashboards.grouping.salary' },
        { value: 'roger_ai', label: 'dashboards.grouping.roger_ai' },
    ];

    watchEffect(() => {
        viewOptionsStore.initializeViewOptions(props.viewOptions, props.activeViewOptionId);
    });

    watch([reportingPeriod, selectedGrouping], () => {
        localStorage.setItem(STORAGE_KEY, selectedGrouping.value);
        router.reload({
            only: ['stats'],
        });
        // Reset hydration state for new dataset so initial hydration runs with overlay
        hasHydratedOnce.value = false;
        manualRefetching.value = false;
        hydratedThisCycle.value = false;
        hydrationInFlight.value = false;
    });

    watch(
        () => data?.value,
        async (newData) => {
            if (!newData) return;
            const rows = (newData as DashboardTableData[]) || [];
            if (!rows.length) return;

            if (!hasHydratedOnce.value || manualRefetching.value) {
                tableData.value = rows;
            } else {
                tableData.value = mergeBasicPreservingDetails(rows);
            }

            // Only hydrate on initial load here. Auto/manual refetch hydrations are handled in isRefetching watcher
            if (!hasHydratedOnce.value) {
                if (hydrationInFlight.value) {
                    return;
                }

                // If the rows returned from the server are already prehydrated (server included all details),
                // skip the client-side details request.
                const prehydrated = rowsArePrehydrated(tableData.value as DashboardTableData[]);
                if (prehydrated) {
                    hasHydratedOnce.value = true;
                    hydratedThisCycle.value = true;
                    return;
                }

                hydrating.value = true;
                hydrationInFlight.value = true;
                try {
                    await loadCompanyDetails(tableData.value);
                } finally {
                    hydrationInFlight.value = false;
                    hydrating.value = false;
                    hasHydratedOnce.value = true;
                    hydratedThisCycle.value = true;
                }
            }
        },
        { immediate: true },
    );

    // Also hydrate after auto refetch completes even if data reference is unchanged
    watch(
        () => isRefetching?.value,
        async (now, prev) => {
            if (prev && !now) {
                const rows = (data?.value as DashboardTableData[]) || [];
                if (!rows.length) return;

                hydrating.value = !hasHydratedOnce.value || manualRefetching.value;
                if (manualRefetching.value) {
                    // Manual: use rows as-is, keep overlay until done
                    tableData.value = rows;
                    if (!hydrationInFlight.value) {
                        hydrationInFlight.value = true;
                        try {
                            await loadCompanyDetails(tableData.value);
                        } finally {
                            hydrationInFlight.value = false;
                            hydrating.value = false;
                            hasHydratedOnce.value = true;
                            manualRefetching.value = false;
                        }
                    }
                } else {
                    if (hydratedThisCycle.value === true) {
                        // We already hydrated in the data watcher for this cycle; avoid double-calling details
                        hydratedThisCycle.value = false;
                        return;
                    }
                    tableData.value = mergeBasicPreservingDetails(rows);
                    if (!hydrationInFlight.value) {
                        hydrationInFlight.value = true;
                        try {
                            await loadCompanyDetails(tableData.value);
                        } finally {
                            hydrationInFlight.value = false;
                        }
                    }
                }
            }
        }
    );
</script>

<template>
    <Head :title="$t('dashboards.title')" />

    <AppLayout
        v-if="hasPermission(Permission.DASHBOARD_ADMIN_VIEW)"
        :breadcrumbs="breadcrumbs"
    >
        <template #header-actions>
            <DashboardStats
                :table="dataTableRef?.table"
                :stats="props.stats"
            />
            <Separator
                orientation="vertical"
                class="mr-4 h-2.5!"
            />
            <MonthPicker v-model="reportingPeriod" />
        </template>
        <PageCard>
            <DataTable
                ref="dataTableRef"
                :columns="
                    columns({
                        statusOptions: statusOptions,
                        reportingPeriod: reportingPeriod,
                        selectedGrouping: selectedGrouping,
                    })
                "
                :data="tableData"
                :loading="isPending || (manualRefetching && !hydrating)"
                :is-error="isError"
                :manual-pagination="true"
                :enable-filters="false"
                :pinned-columns="{
                    left: ['select', 'name'],
                }"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
                :default-pagination="{
                    pageIndex: 0,
                    pageSize: data?.length,
                }"
                :default-column-visibility="{
                    id: false,
                }"
                hide-controls
                :fixedColumnSizes="{
                    keepers_employees: 135,
                }"
                :meta="{ statusOptions }"
                :hydrating="hydrating"
                :autoComputeColumnSizes="{
                    name: 124,
                    bookkeeping_status: 40,
                    report_status: 40,
                    vat_status: 40,
                    weekday: 0,
                }"
                :virtualize="true" 
                :virtualRowHeight="52" 
                :virtualOverscan="8"
            >
                <template #title>
                    <TableTypeSelect
                        v-model="selectedGrouping"
                        :options="typeOptions"
                    />
                </template>
                <template #filters="{ table }">
                    <DataTableAdvancedViewOptions
                        :table="table"
                        :table-identifier="QueryKey.EMPLOYEE_DASHBOARD"
                    />
                </template>
                <template #actions>
                    <DataTableRefreshButton
                        @refetch="handleRefetch"
                        :is-fetching="isFetching || manualRefetching"
                    />
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
